import React, { useEffect, useRef, useState, memo } from "react";
import Radium from "radium";
import PropTypes from "prop-types";
import PercentChanged from "../../percentChanged/percentChanged";
import HandsonTable from "../../handsonTable/handsonTable";
import CountUp from "react-countup";
import TableSkeletonLoader from "../../tableSkeletonLoader/tableSkeletonLoader";
import isEmpty from "lodash/isEmpty";
import DataDisplay from "../../dataDisplay/dataDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const styledTableChildHeader = (row) => {
  let cp = {};
  let childHeader = (row - 1) % 3 == 0;
  if (childHeader) cp = { className: "childHeader" };
  return cp;
};

const HeatMapModal = ({ modalData, tableData, tableDataLoading, handleModalClose, isUserQa }) => {
  const [tableWidth, setTableWidth] = useState(693);
  const tableRef = useRef(null);

  const resizeTable = (node) => {
    if (node) {
      const width = node.closest("#metric-modal")?.clientWidth;
      setTableWidth(width);
    }
  };

  useEffect(() => {
    const globalOverlay = document.getElementById("globalOverlay");
    if (!globalOverlay) return;
    globalOverlay.classList.add("active");

    globalOverlay.addEventListener("click", handleModalClose);

    return () => {
      globalOverlay.removeEventListener("click", handleModalClose);
      globalOverlay.classList.remove("active");
    };
  }, []);

  useEffect(() => {
    resizeTable(tableRef.current);
  }, [tableRef.current]);

  let duration = isUserQa ? 0 : 1;
  let separator = ",";

  if (isEmpty(modalData)) {
    return null;
  }

  const isValueSymbolASuffix = modalData?.title?.valueSymbol === "%";
  const displayValue = getDisplayValue(modalData?.title?.value);
  const dataDisplayCustomValue = !modalData.totalClasses && modalData.showAverages ? "--" : null;

  return (
    <div id="metric-modal" style={{ ...styles.modal }}>
      <div className="heat-map-modal">
        <span
          className="close"
          style={{ ...styles.closeButton }}
          onClick={() => handleModalClose()}
        >
          <FontAwesomeIcon icon={faTimes} />
        </span>
        <div className="top-section">
          <div className="section width-50 justify-between flex-initial">
            <div className="metrics">
              <div className="title" style={styles.title} data-testid="custom-data-title">
                {modalData?.title?.name}
              </div>
              <p style={styles.metric.large} data-testid="custom-data">
                {!displayValue && modalData.showAverages ? (
                  "--"
                ) : (
                  <CountUp
                    suffix={isValueSymbolASuffix ? modalData?.title?.valueSymbol : ""}
                    prefix={!isValueSymbolASuffix ? modalData?.title?.valueSymbol : ""}
                    separator={separator}
                    duration={duration}
                    decimals={modalData?.selectedCategory === "average_class_size" ? 1 : 0}
                    end={displayValue}
                  />
                )}
              </p>
            </div>
            <>
              {modalData?.showTotalClasses ? (
                <div className="metrics">
                  <DataDisplay
                    label="Total Classes"
                    size={40}
                    testId="total-classes"
                    value={modalData.totalClasses}
                    disableAnimation={isUserQa}
                    customValue={dataDisplayCustomValue}
                    sectionProps={{ style: { margin: 0, fontSize: 12 } }}
                    customStyles={{ lineHeight: "1.1" }}
                    averageProps={
                      modalData.avgTotalClasses
                        ? { average: modalData.avgTotalClasses, toolTip: modalData.averageTooltip }
                        : null
                    }
                  />
                </div>
              ) : null}
              {modalData?.showNumberOfClients ? (
                <div className="metrics">
                  <DataDisplay
                    label="# of Clients"
                    size={40}
                    testId="num-of-clients"
                    value={modalData.numberOfClients}
                    customValue={dataDisplayCustomValue}
                    disableAnimation={isUserQa}
                    customStyles={{ lineHeight: "1.1" }}
                    sectionProps={{ style: { margin: 0, fontSize: 12 } }}
                    averageProps={
                      modalData.avgNumberOfClients
                        ? {
                          average: modalData.avgNumberOfClients,
                          toolTip: modalData.averageTooltip
                        }
                        : null
                    }
                  />
                </div>
              ) : null}
            </>
          </div>
          <div className="section width-50">
            <div className="metrics metric-item">
              {!modalData?.showAverages || modalData.value ? (
                <>
                  <p className="title" style={styles.title}>
                    {modalData.mainMetricTitle}
                  </p>
                  <div className="flex items-center">
                    <div className="heat-map-progress-bar">
                      <div
                        className="progress-indicator"
                        style={{
                          ...styles.progress.bar,
                          minWidth: "0%",
                          width: formatPercent(modalData.value)
                        }}
                        data-testid="progress-indicator"
                      >
                        <p style={styles.progress.percent}>
                          {modalData.progressInPercent ? (
                            <CountUp suffix="%" end={modalData.value * 100} decimals={1} />
                          ) : (
                            <CountUp suffix="" end={modalData.value} decimals={0} />
                          )}
                        </p>
                      </div>
                    </div>

                    {modalData.showPrevPeriod ? (
                      <PercentChanged
                        style={styles.percentChanged}
                        fontSize="12px"
                        changeValue={modalData.valueChange}
                        hidePercentIfLessOne={false}
                        digits={1}
                      />
                    ) : null}
                  </div>
                </>
              ) : null}
              {modalData.showPrevPeriod ? (
                <>
                  <p className="title prev" style={styles.title.prev}>
                    Previous Period
                  </p>
                  <div className="heat-map-progress-bar" style={{ ...styles.progress }}>
                    <div
                      className="progress-indicator"
                      style={{
                        ...styles.progress.bar.prev,
                        minWidth: "0%",
                        width: formatPercent(modalData.valuePrevPeriod)
                      }}
                      data-testid="prev-period-progress-indicator"
                    >
                      <p className="prev-period-percent" style={styles.progress.percent}>
                        {modalData.progressInPercent ? (
                          <CountUp suffix="%" end={modalData.valuePrevPeriod * 100} decimals={1} />
                        ) : (
                          <CountUp suffix="" end={modalData.valuePrevPeriod} decimals={0} />
                        )}
                      </p>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            {modalData?.averageRevenue !== 0 &&
              !modalData.showAverages &&
              modalData?.averageRevenuePrevPeriod !== 0 &&
              modalData.showAverageRevenue && (
                <div className="metrics">
                  <p className="title" style={styles.title}>
                    Avg. Revenue
                  </p>
                  <div className="metric-wrapper flex items-center">
                    <p style={styles.metric.large}>
                      {modalData?.currencySymbol}
                      <CountUp
                        separator={separator}
                        duration={duration}
                        end={modalData?.averageRevenue}
                      />
                    </p>
                    <PercentChanged
                      style={styles.percentChanged}
                      fontSize="12px"
                      changeValue={modalData?.averageRevenueChange}
                    />
                  </div>
                  <p
                    className="title prev"
                    style={{ ...styles.title.prev, ...styles.title.prev.noPadding }}
                  >
                    Previous Period
                  </p>
                  <p className="revenue prev" style={styles.revenue.prev}>
                    {modalData?.currencySymbol}
                    <CountUp
                      separator={separator}
                      duration={duration}
                      end={modalData?.averageRevenuePrevPeriod}
                    />
                  </p>
                </div>
              )}
          </div>
        </div>
        <div className="bottom-section">
          {!tableDataLoading && tableData?.data.length ? (
            <p className="class-title">{modalData.detailsTitle}</p>
          ) : null}
          <div className="table-wrapper">
            <div
              className="table-holder"
              style={{
                opacity: tableDataLoading ? 1 : 0,
                height: tableDataLoading ? 1 : 0
              }}
            >
              <TableSkeletonLoader />
            </div>
            <div
              ref={tableRef}
              className="table-holder"
              style={{ opacity: !tableDataLoading ? 1 : 0 }}
            >
              {!tableDataLoading ? (
                tableData.data.length ? (
                  <HandsonTable
                    data={tableData.data}
                    colHeaders={tableData.colHeaders}
                    nestedRows={tableData.nestedRows}
                    rowHeaders={() => ""}
                    cells={tableData.nestedRows ? (row) => styledTableChildHeader(row) : false}
                    rowHeaderWidth={20}
                    width={tableWidth}
                    height={modalData.tableHeight}
                    stretchH="all"
                    customRenderer={() => console.warn}
                    collapseAllNestedRows={tableData.nestedRows}
                  />
                ) : (
                  <p style={styles.noDataMessage} data-testid="no-data-message">
                    No data for selected period
                  </p>
                )
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

HeatMapModal.propTypes = {
  tableData: PropTypes.object,
  modalData: PropTypes.object,
  totalClasses: PropTypes.number,
  numberOfClients: PropTypes.number,
  averageRevenue: PropTypes.number,
  averageRevenueChange: PropTypes.number,
  averageRevenuePrevPeriod: PropTypes.number,
  tableDataLoading: PropTypes.bool,
  tableHeight: PropTypes.number
};

export function getDisplayValue(data) {
  let value = data;
  if (typeof value == "string") {
    if (data.endsWith(")")) {
      let valuesArray = data.trim().split(" ");
      value = valuesArray[0];
    }
    if (value === "--") {
      return null;
    }
    if (value.endsWith("%")) {
      let split = value.split("%");
      value = split[0];
    }
  }

  return Number(value);
}

export function formatPercent(data) {
  if (data > 1) {
    return "100%";
  } else {
    return data * 100 + "%";
  }
}

const styles = {
  title: {
    color: "#8792a1",
    fontWeight: "700",
    textTransform: "uppercase",
    fontSize: "12px",
    lineHeight: 1.1,
    prev: {
      color: "#5e6570",
      textTransform: "uppercase",
      fontWeight: "700",
      fontSize: "10px",
      paddingTop: "7px",
      paddingBottom: "0px",
      margin: "0",
      noPadding: {
        paddingTop: "2px"
      }
    }
  },
  metric: {
    color: "#448be4",
    large: {
      color: "#448be4",
      fontSize: "40px",
      fontWeight: "200",
      lineHeight: 1.1
    }
  },
  progress: {
    height: "15px",
    backgroundColor: "rgb(93, 101, 118)",
    bar: {
      position: "relative",
      height: "15px",
      backgroundColor: "#0097cf",
      minWidth: "20%",
      prev: {
        position: "relative",
        height: "15px",
        backgroundColor: "#f26c4f",
        minWidth: "20%"
      }
    },
    percent: {
      fontSize: "10px",
      fontWeight: "600",
      textAlign: "right",
      paddingRight: "5px",
      position: "relative",
      top: "-5px"
    }
  },
  percentChanged: {
    alignSelf: "flex-end",
    paddingBottom: "10px"
  },
  revenue: {
    prev: {
      color: "#448be4",
      marginTop: "-4px"
    }
  },
  noDataMessage: {
    textAlign: "center",
    fontSize: "35px",
    color: "#86919F",
    height: "400px",
    lineHeight: "300px"
  },
  closeButton: {
    color: "white",
    position: "absolute",
    right: "15px",
    top: "15px",
    cursor: "pointer"
  },
  modal: {
    padding: "35px 20px 0px 35px",
    minWidth: "1000px",
    height: "560px",
    position: "fixed",
    background: "#202229",
    transition: "opacity 0.5s",
    borderRadius: "10px",
    border: "solid 1px #353942",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 162
  }
};

export default memo(Radium(HeatMapModal));
